import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// A React component for displaying a popup with data 
// charts and details on mobile devices. Shows toxin 
// and satellite data for a selected location in a mobile-friendly format.

// Register required components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const MobileDataPopup = ({ location, isOpen, onClose, filteredMeasurements, locations }) => {
  const [locationData, setLocationData] = useState([]);
  const [satelliteData, setSatelliteData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('toxins');

  // Filter data for the selected location and convert to expected format
  useEffect(() => {
    if (location && filteredMeasurements && locations) {
      // Filter measurements for this location
      const locationMeasurements = filteredMeasurements.filter(
        measurement => {
          const loc = locations.find(l => l.id === measurement.location_id);
          return loc && loc.name === location.name;
        }
      );
      
      // Convert to the format expected by the chart component
      const formattedData = locationMeasurements.map(measurement => {
        const loc = locations.find(l => l.id === measurement.location_id);
        return {
          properties: {
            name: loc?.name || 'Unknown',
            amount: measurement.value,
            date: measurement.date,
            toxin: measurement.toxin_type || 'DSP',
            id: measurement.location_id
          }
        };
      });
      
      setLocationData(formattedData);
    }
  }, [location, filteredMeasurements, locations]);

  // Fetch satellite data for the location (you might need to implement this API call)
  useEffect(() => {
    if (location && isOpen) {
      fetchSatelliteData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, isOpen]);

  const fetchSatelliteData = async () => {
    setIsLoading(true);
    try {
      // This would be your satellite data API endpoint
      // For now, we'll use mock data or skip if API isn't available
      const apiBase = process.env.REACT_APP_MEASUREMENTS_API?.replace('/measurements', '') || 'http://localhost:8000';
      const response = await fetch(`${apiBase}/satellite/location/${location.id}`);
      
      if (response.ok) {
        const data = await response.json();
        setSatelliteData(data.satellite_data || []);
      }
    } catch (error) {
      console.log('Satellite data not available');
      setSatelliteData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderToxinChart = () => {
    if (!locationData || locationData.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>No toxin data available</p>
          <p className="text-sm">for {location?.name}</p>
        </div>
      );
    }

    // Prepare chart data
    const dates = locationData.map(item => item.properties.date).sort();
    const values = dates.map(date => {
      const found = locationData.find(item => item.properties.date === date);
      return found ? found.properties.amount : null;
    });

    const chartData = {
      labels: dates.map(date => new Date(date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      })),
      datasets: [{
        label: 'DSP Toxin Level (μg/g)',
        data: values,
        fill: false,
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        tension: 0.1,
        pointRadius: 4,
        pointBackgroundColor: 'rgba(59, 130, 246, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1,
          cornerRadius: 6
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Date',
            font: { size: 12 }
          },
          ticks: {
            font: { size: 10 }
          }
        },
        y: {
          title: {
            display: true,
            text: 'μg/g',
            font: { size: 12 }
          },
          beginAtZero: true,
          ticks: {
            font: { size: 10 }
          }
        }
      }
    };

    return (
      <div className="h-64">
        <Line data={chartData} options={options} />
      </div>
    );
  };

  const renderSatelliteChart = () => {
    if (!satelliteData || satelliteData.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>No satellite data available</p>
          <p className="text-sm">for {location?.name}</p>
        </div>
      );
    }

    // Separate SST and Chlorophyll data
    const sstData = satelliteData.filter(d => d.data_type === 'sst');
    const chlorophyllData = satelliteData.filter(d => d.data_type === 'chlorophyll');

    const allDates = Array.from(new Set(satelliteData.map(item => item.date))).sort();
    const datasets = [];

    if (sstData.length > 0) {
      const sstValues = allDates.map(date => {
        const found = sstData.find(item => item.date === date);
        return found ? found.value : null;
      });

      datasets.push({
        label: 'Temperature (°C)',
        data: sstValues,
        fill: false,
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 2,
        tension: 0.1,
        pointRadius: 3,
        pointBackgroundColor: 'rgba(239, 68, 68, 1)',
        yAxisID: 'y'
      });
    }

    if (chlorophyllData.length > 0) {
      const chlorophyllValues = allDates.map(date => {
        const found = chlorophyllData.find(item => item.date === date);
        return found ? found.value : null;
      });

      datasets.push({
        label: 'Chlorophyll (mg/m³)',
        data: chlorophyllValues,
        fill: false,
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 2,
        tension: 0.1,
        pointRadius: 3,
        pointBackgroundColor: 'rgba(34, 197, 94, 1)',
        yAxisID: 'y1'
      });
    }

    const chartData = {
      labels: allDates.map(date => new Date(date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      })),
      datasets
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            boxWidth: 12,
            font: { size: 10 }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Date',
            font: { size: 12 }
          },
          ticks: {
            font: { size: 10 }
          }
        },
        y: {
          type: 'linear',
          display: sstData.length > 0,
          position: 'left',
          title: {
            display: true,
            text: 'Temperature (°C)',
            font: { size: 12 }
          },
          ticks: {
            font: { size: 10 }
          }
        },
        y1: {
          type: 'linear',
          display: chlorophyllData.length > 0,
          position: 'right',
          title: {
            display: true,
            text: 'Chlorophyll (mg/m³)',
            font: { size: 12 }
          },
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            font: { size: 10 }
          }
        }
      }
    };

    return (
      <div className="h-64">
        <Line data={chartData} options={options} />
      </div>
    );
  };

  const renderDataTable = () => {
    if (!locationData || locationData.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>No data available</p>
        </div>
      );
    }

    return (
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {locationData.map((item, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-3 border">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-medium text-gray-900">
                  {item.properties.amount} μg/g
                </div>
                <div className="text-sm text-gray-600">
                  {new Date(item.properties.date).toLocaleDateString()}
                </div>
                {item.properties.toxin && (
                  <div className="text-xs text-blue-600 mt-1">
                    {item.properties.toxin}
                  </div>
                )}
              </div>
              <div className={`px-2 py-1 rounded text-xs font-medium ${
                item.properties.amount < 80 
                  ? 'bg-green-100 text-green-800' 
                  : item.properties.amount < 160 
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
              }`}>
                {item.properties.amount < 80 ? 'Safe' : 
                 item.properties.amount < 160 ? 'Caution' : 'Dangerous'}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen || !location) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[80]"
        onClick={onClose}
      />
      
      {/* Popup */}
      <div className="fixed inset-x-0 bottom-0 z-[90] bg-white rounded-t-2xl shadow-2xl max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {location.name}
            </h2>
            <p className="text-sm text-gray-500">Pollution Data & Analysis</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 bg-white sticky top-[73px] z-10">
          <button
            onClick={() => setActiveTab('toxins')}
            className={`flex-1 py-3 px-4 text-sm font-medium ${
              activeTab === 'toxins'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Toxin Levels
          </button>
          <button
            onClick={() => setActiveTab('satellite')}
            className={`flex-1 py-3 px-4 text-sm font-medium ${
              activeTab === 'satellite'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Satellite Data
          </button>
          <button
            onClick={() => setActiveTab('data')}
            className={`flex-1 py-3 px-4 text-sm font-medium ${
              activeTab === 'data'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Raw Data
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 146px)' }}>
          {isLoading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading data...</p>
            </div>
          )}

          {!isLoading && (
            <>
              {activeTab === 'toxins' && renderToxinChart()}
              {activeTab === 'satellite' && renderSatelliteChart()}
              {activeTab === 'data' && renderDataTable()}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MobileDataPopup;
